import { useMemo } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { ArrayItemsEditor } from './ArrayItemsEditor';
import type { PropertyData } from '@/domain/entities/property-object';
import { LocationType, type IHomeLocation } from '@/domain/entities/home-location';
import { AreaValueField } from '@/components/ui/AreaValueField';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/root-state';

import {
  fetchLocationsByPropertyId,
  createLocation,
  updateLocation as updateLocationThunk,
  deleteLocation as deleteLocationThunk,
} from '@/features/locations/locationsThunks';

import {
  selectLocationsByPropertyId,
  selectLocationsListingByPropertyId,
  selectLocationsFetchedByPropertyId,
  setLocationsForProperty,
  selectDirtyByPropertyId,
  markLocationDirty,
  markLocationClean,
  selectLocationsActiveSort,
  setLocationsActiveSort,
} from '@/features/locations/locationsSlice';

import { buildChildrenIndex, collectDescendants } from '../propertyEditorUtils';
import { sortItems } from '@/lib';
import { PerItemSaveActions } from './shared/PerItemSaveActions';
import {
  buildPropertyEditorPersistKey,
  getDirtyBorderClass,
  isTempId,
  reconcileKeepOrder,
  removeAtIndex,
} from './shared/helpers';
import { useSortedItemsOnce } from './shared/useSortedItemsOnce';
import { usePropertyEntityTabController } from './shared/usePropertyEntityTabController';
import { TabLoadBanner } from './shared/TabLoadBanner';
import { TypeNames } from '@/domain/shared/storable';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';

type BuilderMode = 'create' | 'edit';

function nowSec() {
  return Math.floor(Date.now() / 1000);
}

function makeNewLocation(propertyID: string): IHomeLocation {
  const t = nowSec();

  return {
    id: `tmp-location-${t}`,
    typeName: TypeNames.Location,
    createdAt: t,
    updatedAt: t,

    title: 'New Location',
    description: null,
    thumbnail: null,
    thumbnailUrl: null,
    photos: null,

    area: undefined,

    propertyID,
    parentID: propertyID,
    type: LocationType.Custom,
  };
}

const patchItem = (item: IHomeLocation, patch: Partial<IHomeLocation>): IHomeLocation => ({
  ...item,
  ...patch,
  updatedAt: nowSec(),
});

function LocationEditorCard(props: {
  item: IHomeLocation;
  items: IHomeLocation[];

  mode: BuilderMode;
  propertyTitle: string;
  resolvedPropertyID: string;

  childrenByParent: ReturnType<typeof buildChildrenIndex>;

  // helpers
  patchItem: (item: IHomeLocation, patch: Partial<IHomeLocation>) => IHomeLocation;
  setItem: (next: IHomeLocation) => void;
  markDirty: (id: string) => void;
}) {
  const { item, items, mode, propertyTitle, resolvedPropertyID, childrenByParent, patchItem, setItem, markDirty } =
    props;

  const set = (patch: Partial<IHomeLocation>) => {
    const next = patchItem(item, patch);

    // update ArrayItemsEditor internal copy for immediate UI
    setItem(next);

    if (mode !== 'edit' || !next.id || !resolvedPropertyID) return;

    // ✅ mark dirty in store
    markDirty(next.id);
  };

  const propertyID = resolvedPropertyID || 'unknown-property';
  const selfId = item.id ?? '';

  const descendants = selfId ? collectDescendants({ startId: selfId, childrenByParent }) : new Set<string>();

  const isForbiddenParent = (candidateId: string) => {
    if (!selfId) return false;
    if (candidateId === selfId) return true;
    return descendants.has(candidateId);
  };

  const parentOptions = [
    { id: propertyID, title: `🏠 ${propertyTitle}` },
    ...items
      .filter((l) => Boolean(l.id))
      .filter((l) => !isForbiddenParent(l.id!))
      .map((l) => ({
        id: l.id!,
        title: l.title || '(Untitled location)',
      })),
  ];

  const parentValue = item.parentID ?? propertyID;

  return (
    <div className="space-y-6">
      {/* Title + Type */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            placeholder="e.g., Primary Bedroom"
            value={item.title ?? ''}
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>Type</Label>
          <Select value={item.type} onValueChange={(type) => set({ type: type as LocationType })}>
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {Object.values(LocationType).map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          placeholder="Optional notes about this location"
          value={item.description ?? ''}
          onChange={(e) => set({ description: e.target.value || null })}
        />
      </div>

      {/* Area + thumbnails */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <AreaValueField value={item.area} onChange={(next) => set({ area: next })} />
          <p className="text-muted-foreground text-xs">Canonical is stored in sqm based on your AreaValueField.</p>
        </div>

        <div className="space-y-2">
          <Label>Thumbnail</Label>
          <Input
            placeholder="Not set"
            value={item.thumbnail ?? ''}
            onChange={(e) => set({ thumbnail: e.target.value || null })}
          />
        </div>

        <div className="space-y-2">
          <Label>Thumbnail URL</Label>
          <Input
            placeholder="Not set"
            value={item.thumbnailUrl ?? ''}
            onChange={(e) => set({ thumbnailUrl: e.target.value || null })}
          />
        </div>
      </div>

      {/* Photos / Documents (still placeholder) */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Photos</Label>
          <Textarea placeholder="Not set (wired later)" value={''} readOnly />
        </div>

        <div className="space-y-2">
          <Label>Documents</Label>
          <Textarea placeholder="Not set (wired later)" value={''} readOnly />
        </div>
      </div>

      {/* IDs + Parent selector */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label>propertyID</Label>
          <Input value={item.propertyID ?? ''} disabled />
        </div>

        <div className="space-y-2">
          <Label>Parent</Label>
          <Select
            value={parentValue}
            onValueChange={(parentID) =>
              set({
                propertyID: item.propertyID ?? propertyID,
                parentID,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select parent location" />
            </SelectTrigger>
            <SelectContent>
              {parentOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selfId && descendants.size > 0 ? (
            <p className="text-muted-foreground text-xs">
              To prevent cycles, this list excludes this location and its {descendants.size} descendant
              {descendants.size === 1 ? '' : 's'}.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label>id</Label>
          <Input value={item.id ?? ''} disabled />
        </div>
      </div>
    </div>
  );
}

/**
 *
 *  MAIN Component
 *
 */
export function LocationsTab(props: {
  data: PropertyData;
  setData: (next: PropertyData) => void;
  mode: BuilderMode;
  propertyId?: string;
}) {
  const { data, setData, mode, propertyId } = props;
  const dispatch = useAppDispatch();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const canCreate = mode === 'create' || isSuperAdmin || hasPermission(Permissions.LocationsCreate);
  const canUpdate = mode === 'create' || isSuperAdmin || hasPermission(Permissions.LocationsUpdate);
  const canDelete = mode === 'create' || isSuperAdmin || hasPermission(Permissions.LocationsDelete);

  const activeSort = useAppSelector(selectLocationsActiveSort);
  const resolvedPropertyID = data.property?.id ?? propertyId ?? '';
  const propertyTitle = data.property?.title ?? 'Property';
  const dirtyById = useAppSelector((s) => selectDirtyByPropertyId(s, resolvedPropertyID));
  const localItems = data.locations ?? [];
  const storeItems = useAppSelector((s) => selectLocationsByPropertyId(s, resolvedPropertyID));
  const effectiveItems = mode === 'edit' ? storeItems : localItems;
  const isLoading = useAppSelector((s) => selectLocationsListingByPropertyId(s, resolvedPropertyID));
  const hasFetched = useAppSelector((s) => selectLocationsFetchedByPropertyId(s, resolvedPropertyID));
  const error = useAppSelector((s: RootState) =>
    resolvedPropertyID ? (s.propertyLocations.errorByPropertyID[resolvedPropertyID] ?? null) : null,
  );
  const scopeKey = `${mode}:${resolvedPropertyID || 'new'}`;
  const { sortedItems, setSortedItems, resort } = useSortedItemsOnce<IHomeLocation>({
    scopeKey,
    items: effectiveItems,
    enabled: mode === 'edit' ? hasFetched : false,
    activeSort,
    sort: (arr, spec) => sortItems(arr, spec) as IHomeLocation[],
    reconcile: (prev, next) => reconcileKeepOrder(prev, next, { insertNew: 'top' }) as IHomeLocation[],
  });

  const markDirty = (id: string) => {
    if (!resolvedPropertyID) return;
    dispatch(markLocationDirty({ propertyID: resolvedPropertyID, locationID: id }));
  };
  const markClean = (id: string) => {
    if (!resolvedPropertyID) return;
    dispatch(markLocationClean({ propertyID: resolvedPropertyID, id }));
  };

  const ctrl = usePropertyEntityTabController<IHomeLocation>({
    mode,
    resolvedPropertyID,
    localItems,
    storeItems,
    writeLocal: (next) => setData({ ...data, locations: next }),
    writeStore: (next) => dispatch(setLocationsForProperty({ propertyID: resolvedPropertyID, items: next })),
    hasFetched,
    fetchRemote: () => dispatch(fetchLocationsByPropertyId({ propertyID: resolvedPropertyID })),
    markDirty,
    markClean,
    createRemote: (loc, tempId) => dispatch(createLocation({ location: loc, tempId })).unwrap(),
    updateRemote: (loc) =>
      dispatch(updateLocationThunk({ id: loc.id!, propertyID: resolvedPropertyID, patch: loc })).unwrap(),
    deleteRemote: (id) => dispatch(deleteLocationThunk({ id, propertyID: resolvedPropertyID })).unwrap(),
    entityName: 'location',
    isTempId,
    normalizeBeforeSave: (loc) => ({
      ...loc,
      propertyID: loc.propertyID ?? resolvedPropertyID,
      parentID: loc.parentID ?? loc.propertyID ?? resolvedPropertyID,
      updatedAt: nowSec(),
    }),
  });

  // IMPORTANT: keep this based on what the UI uses for parenting (sortedItems)
  const childrenByParent = useMemo(() => buildChildrenIndex(sortedItems), [sortedItems]);

  return (
    <div className="space-y-3">
      <TabLoadBanner mode={mode} isLoading={isLoading} error={error} loadingLabel="Loading locations…" />
      <ArrayItemsEditor<IHomeLocation>
        title="Locations"
        persistKey={buildPropertyEditorPersistKey({ propertyID: resolvedPropertyID, tab: 'locations' })}
        items={sortedItems}
        onChange={ctrl.writeAll}
        createItem={() => makeNewLocation(resolvedPropertyID || 'unknown-property')}
        canAdd={canCreate}
        canEdit={(item) => (isTempId(item.id) ? canCreate : canUpdate)}
        canRemove={(item) => (isTempId(item.id) ? canCreate : canDelete)}
        getItemTitle={(x, idx) => x.title || `Location #${idx + 1}`}
        sortItems={(_items, sortSpec) => {
          setSortedItems(sortItems(_items, sortSpec) as IHomeLocation[]);
          dispatch(setLocationsActiveSort(sortSpec));
        }}
        resortItems={resort}
        activeSort={activeSort}
        getItemClassName={(loc) =>
          getDirtyBorderClass({
            mode,
            id: loc.id,
            dirtyById,
            isTempId,
          })
        }
        rightHeader={({ item }) => {
          if (mode !== 'edit' || !item.id) return null;

          const id = item.id;
          const saving = !!ctrl.savingById[id];
          const canSave = (dirtyById[id] ?? false) || isTempId(id);
          const err = ctrl.saveErrorById[id];
          const mayCreateOrUpdate = isTempId(id) ? canCreate : canUpdate;
          const mayDelete = isTempId(id) ? canCreate : canDelete;

          if (!mayCreateOrUpdate && !mayDelete && !err) return null;

          return (
            <PerItemSaveActions
              error={err}
              saving={saving}
              canSave={canSave}
              saveLabel={isTempId(id) ? 'Save' : 'Update'}
              onDelete={mayDelete ? () => void ctrl.deleteItem(item) : undefined}
              onSave={mayCreateOrUpdate ? () => void ctrl.saveItem(item) : undefined}
            />
          );
        }}
        onRemove={(item, idx) => {
          if (mode === 'edit') {
            void ctrl.deleteItem(item);
            return;
          }
          setData({ ...data, locations: removeAtIndex(data.locations, idx) });
        }}
        onRefresh={() => {
          if (mode !== 'edit') return;
          if (!resolvedPropertyID) return;
          void dispatch(fetchLocationsByPropertyId({ propertyID: resolvedPropertyID }));
        }}
        isRefreshing={isLoading}
        renderItem={({ item, setItem }) => (
          <LocationEditorCard
            item={item}
            items={sortedItems}
            mode={mode}
            propertyTitle={propertyTitle}
            resolvedPropertyID={resolvedPropertyID}
            childrenByParent={childrenByParent}
            patchItem={patchItem}
            setItem={setItem}
            markDirty={markDirty}
          />
        )}
      />
    </div>
  );
}
